import { Box, useTheme } from "@mui/material"
import { ReactElement } from "react"
import { useNavigate } from "react-router-dom"

import { AvatarContentCell } from "./applicationTable"
import { NA } from "../../../constants/generalConstants"
import { TT_UNREAD_COMMENTS } from "../../../constants/tooltips"
import { useScreenSize } from "../../../hooks/useScreenSize"
import { DNO_SINGLE_APPLICATION_BASE } from "../../../router/routes"
import { User } from "../../../services/middleware/hooks/dnoInterface/protected/useGetAssigneeById"
import usePostApplicationAssignment from "../../../services/middleware/hooks/dnoInterface/protected/usePostApplicationAssignment"
import { IApplicationSummary } from "../../../services/middleware/mappers/dnoInterface/dnoApplicationsMapper"
import { scrollToTop } from "../../../utils/submitApplication/submitApplicationUtils"
import AppChip from "../../dataDisplay/chips/appChip"
import CopyTypography from "../../dataDisplay/typography/copyTypography"
import AppTooltip from "../../helper/appTooltip"
import UserSuggestionAutocomplete from "../../inputs/controlled/userSuggestionAutocomplete"
import LctChip from "../../pills/lct/lctChip"
import StatusChip from "../../pills/status/statusChip"
import DisplayIf from "../../wrappers/displayIf"
import AppTableCell from "../appTableCell"
import AppTableContentRow from "../appTableContentRow"

const ADDRESS_MAX_LENGTH = 25

interface IApplicationRowProps {
    application: IApplicationSummary
    isEscalations: boolean
}
/**
 * Single row in application summary table. Includes logic for opening
 * the DNO response form dialogue.
 *
 * Any changes to XL only config must be aligned with headings in
 * `applicationTable.tsx`
 */
const ApplicationRow = ({
    application,
    isEscalations,
}: IApplicationRowProps): ReactElement => {
    const navigate = useNavigate()
    const { isXl } = useScreenSize()
    const theme = useTheme()
    const addressTruncated =
        application.address.length > ADDRESS_MAX_LENGTH
            ? `${application.address.substring(0, ADDRESS_MAX_LENGTH)}...`
            : application.address

    const { postUsersId } = usePostApplicationAssignment(
        application.applicationId
    )

    const assignUser = (
        newValue: User | null,
        resetOnFail: () => void
    ): void => {
        postUsersId(newValue?.username || "", resetOnFail)
    }

    return (
        <AppTableContentRow
            key={application.applicationId}
            hover
            onClick={() => {
                navigate(
                    `${DNO_SINGLE_APPLICATION_BASE}/${application.applicationId}`
                )
                scrollToTop(document)
            }}
            sx={{
                cursor: "pointer",
            }}
        >
            <AvatarContentCell lcts={application.lcts} />

            <AppTableCell>
                <CopyTypography
                    text={addressTruncated}
                    customCopyText={application.address}
                    typographyProps={{ variant: "body2" }}
                    customTooltip={`${application.address} - click to copy to clipboard!`}
                />
            </AppTableCell>
            <AppTableCell>{application.postCode}</AppTableCell>
            <AppTableCell>{application.installerCompany}</AppTableCell>
            <AppTableCell>
                {isEscalations ? (
                    <AppChip
                        label={application.submittedAgo}
                        variant="filled"
                        color={application.submittedAgoRag}
                    />
                ) : (
                    application.lastUpdateDate
                )}
            </AppTableCell>
            <AppTableCell align="center">
                <StatusChip status={application.applicationStatus} />
            </AppTableCell>
            <DisplayIf condition={isXl}>
                <>
                    <AppTableCell align="center">
                        {application.totAggGen === NA
                            ? " - "
                            : `${application.totAggGen} kW`}
                    </AppTableCell>
                    <AppTableCell align="center">{`${application.maxDemand} A`}</AppTableCell>
                </>
            </DisplayIf>
            <AppTableCell>
                {application.lcts.map((lct) => (
                    <LctChip
                        sx={{
                            mr: 0.2,
                            mb: application.lcts.length > 0 ? 1 : 0,
                        }}
                        key={lct}
                        lct={lct}
                    />
                ))}
            </AppTableCell>
            <DisplayIf condition={isXl}>
                <AppTableCell onClick={(event) => event.stopPropagation()}>
                    <UserSuggestionAutocomplete
                        onSelectedUserChange={assignUser}
                        userId={application.assignedTo}
                        sx={{ minWidth: "250px", mt: -2 }}
                    />
                </AppTableCell>
            </DisplayIf>
            <AppTableCell align="center" width={50}>
                {application.isDNOThreadUnread && (
                    <AppTooltip title={TT_UNREAD_COMMENTS}>
                        <Box
                            sx={{
                                width: 14,
                                height: 14,
                                backgroundColor: theme.palette.primary.main,
                                borderRadius: "50%",
                                display: "inline-block",
                            }}
                        />
                    </AppTooltip>
                )}
            </AppTableCell>
        </AppTableContentRow>
    )
}

export default ApplicationRow
